import { prepareHeaders } from '@/lib/cookie'
import { BASE_URL } from '@/constant'
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'

export type PlanStatus = 'no_plan' | 'draft' | 'submitted'

export interface WPMember {
  id: number
  name: string
  email: string
  team_id: number | null
  team: string
  is_team_leader: boolean
  plan_id: number | null
  plan_status: PlanStatus
}

export interface WPMembersResponse {
  success: boolean
  count: number
  data: WPMember[]
}

export interface WPPlanRow {
  user_id: number
  member_name: string
  team_id: number | null
  team: string
  plan_id: number | null
  status: string | null
  plan_status: PlanStatus
  est_bench_date: string | null
  notes: string | null
  est_billing_hrs: string
  est_non_billing_hrs: string
  backlog_hrs: string
  achieved_billable_hrs: string
}

export interface WPPlansResponse {
  success: boolean
  week_start: string
  week_end: string
  data: WPPlanRow[]
}

export interface WPPlanTask {
  id?: number
  clickup_task_id: string | null
  task_name: string
  project_id: number
  project_name?: string
  is_billable: boolean
  estimated_hours: number | string
  is_backlog: boolean
}

export interface WPProjectGroup {
  project_id: number
  project_name: string
  total_hours: number
  tasks: {
    id: number
    clickup_task_id: string | null
    task_name: string
    is_billable: boolean
    estimated_hours: string
  }[]
}

export interface WPPlanDetail {
  id: number
  member_user_id: number
  member_name: string
  team_id: number | null
  team: string
  week_start: string
  week_end: string
  status: string
  est_bench_date: string | null
  notes: string | null
  created_by: number
  created_at: string
  updated_at: string

  previous_plan_id: number | null
  next_plan_id: number | null

  plan_projects: WPProjectGroup[]
  backlog_projects: WPProjectGroup[]
}
export interface WPPlanDetailResponse {
  success: boolean
  data: WPPlanDetail
}

export interface WPTaskPayload {
  project_id: number
  clickup_task_id: string | null
  task_name: string
  is_billable: boolean
  estimated_hours: number
  is_backlog: boolean
}

export interface WPCreatePlanPayload {
  member_user_id: number
  week_start: string
  est_bench_date?: string | null
  notes?: string | null
  tasks: WPTaskPayload[]
}

export interface WPUpdatePlanPayload {
  id: number
  est_bench_date?: string | null
  notes?: string | null
  submit?: boolean
  tasks?: WPTaskPayload[]
}

export interface WPProject {
  id: number
  name: string
  list_id: number
  project_type: string
  status: string | null
  team_id: number | null
  team: string | null
  is_assigned: 0 | 1
}

export interface WPBacklogProject {
  id: number
  name: string
  list_id: number
  project_type: string
  status: string | null
  team: string | null
}

export interface WPProjectsResponse {
  success: boolean
  backlog_projects: WPBacklogProject[]
  data: WPProject[]
}

export interface WPProjectsQueryArgs {
  search?: string
  member_user_id?: number
  include_inactive?: boolean
}

export interface WPBacklogProjectsResponse {
  success: boolean
  data: WPBacklogProject[]
}

export interface WPTask {
  clickup_task_id: string
  task_name: string
  status: string
  time_estimate: number | null
}

export interface WPTasksResponse {
  success: boolean
  project_id: number
  data: WPTask[]
}

export interface WPTaskStatusesResponse {
  success: boolean
  project_id: number
  data: string[]
}

export interface WPPreviousWeekReviewMember {
  user_id: number
  name: string
  team: string | null
}

export interface WPPreviousWeekRange {
  week_start: string
  week_end: string
}

export interface WPPreviousWeekPlanSummary {
  id: number
  status: string
  est_bench_date: string | null
  notes: string | null
  reviewed_at: string | null
  created_at: string
  updated_at: string
}

export interface WPPreviousWeekTask {
  id: number
  clickup_task_id: string | null
  task_name: string
  is_billable: 0 | 1
  estimated_hours: number
  clickup_time_estimate: number | null
  actual_hours: number
}

export interface WPPreviousWeekProjectGroup {
  project_id: number | null
  project_name: string | null
  total_hours: number
  tasks: WPPreviousWeekTask[]
}

export interface WPUnplannedActual {
  clickup_task_id: string | null
  task_name: string
  is_billable: boolean
  actual_hours: number
}

export interface WPPreviousWeekTotals {
  planned_billable_hrs: number
  planned_non_billable_hrs: number
  planned_backlog_hrs: number
  actual_billable_hrs: number
  actual_non_billable_hrs: number
}

export interface WPPreviousWeekReview {
  member: WPPreviousWeekReviewMember
  previous_week: WPPreviousWeekRange
  plan: WPPreviousWeekPlanSummary | null
  acknowledgement_required: boolean
  planned_projects: WPPreviousWeekProjectGroup[]
  backlog_projects: WPPreviousWeekProjectGroup[]
  unplanned_actuals: WPUnplannedActual[]
  totals: WPPreviousWeekTotals
}

export interface WPPreviousWeekReviewResponse {
  success: boolean
  data: WPPreviousWeekReview
}

export interface WPPreviousWeekArgs {
  member_user_id: number
  week_start: string
}

export interface WPAcknowledgeResponse {
  success: boolean
  message: string
  data?: { reviewed_at: string }
}

export const weeklyPlanningApi = createApi({
  reducerPath: 'weeklyPlanningApi',
  baseQuery: fetchBaseQuery({
    baseUrl: BASE_URL,
    prepareHeaders,
  }),
  tagTypes: ['WPPlans', 'WPPlan', 'WPReview'],
  endpoints: builder => ({
    getWPPlans: builder.query<
      WPPlansResponse,
      { week_start: string; team_id?: number }
    >({
      query: ({ week_start, team_id }) => ({
        url: '/weekly-planning/plans',
        params: {
          week_start,
          ...(team_id !== undefined && { team_id }),
        },
      }),
      providesTags: ['WPPlans'],
    }),
    getWPPlanDetail: builder.query<WPPlanDetailResponse, number>({
      query: id => `/weekly-planning/plans/${id}`,
      providesTags: (_, __, id) => [{ type: 'WPPlan', id }],
    }),
    createWPPlan: builder.mutation<
      {
        success: boolean
        message: string
        data: {
          plan_id: number
          status: string
          week_start: string
          week_end: string
        }
      },
      WPCreatePlanPayload
    >({
      query: payload => ({
        url: '/weekly-planning/plans',
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: ['WPPlans'],
    }),
    updateWPPlan: builder.mutation<
      {
        success: boolean
        message: string
        data: { plan_id: number; status: string }
      },
      WPUpdatePlanPayload
    >({
      query: ({ id, ...body }) => ({
        url: `/weekly-planning/plans/${id}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (_, __, { id }) => ['WPPlans', { type: 'WPPlan', id }],
    }),
    getWPProjects: builder.query<WPProjectsResponse, WPProjectsQueryArgs>({
      query: ({ search, member_user_id, include_inactive }) => ({
        url: '/weekly-planning/projects',
        params: {
          ...(search && { search }),
          ...(member_user_id != null && { member_user_id }),
          ...(include_inactive && { include_inactive: true }),
        },
      }),
    }),
    getWPBacklogProjects: builder.query<WPBacklogProjectsResponse, void>({
      query: () => ({
        url: '/weekly-planning/backlog-projects',
      }),
    }),
    getWPTasks: builder.query<
      WPTasksResponse,
      { project_id: number; statuses?: string[] }
    >({
      query: ({ project_id, statuses }) => {
        let url = `/weekly-planning/tasks?project_id=${project_id}`
        if (statuses && statuses.length > 0) {
          const encoded = statuses.map(s => encodeURIComponent(s)).join(',')
          url += `&statuses=${encoded}`
        }
        return { url }
      },
    }),
    getWPTaskStatuses: builder.query<WPTaskStatusesResponse, number>({
      query: project_id => ({
        url: '/weekly-planning/task-statuses',
        params: { project_id },
      }),
    }),
    getWPPreviousWeekReview: builder.query<
      WPPreviousWeekReviewResponse,
      WPPreviousWeekArgs
    >({
      query: ({ member_user_id, week_start }) => ({
        url: '/weekly-planning/previous-week-review',
        params: { member_user_id, week_start },
      }),
      providesTags: (_, __, { member_user_id, week_start }) => [
        { type: 'WPReview', id: `${member_user_id}-${week_start}` },
      ],
    }),
    acknowledgeWPPreviousWeek: builder.mutation<
      WPAcknowledgeResponse,
      WPPreviousWeekArgs
    >({
      query: body => ({
        url: '/weekly-planning/plans/acknowledge-previous-week',
        method: 'POST',
        body,
      }),
      invalidatesTags: (_, __, { member_user_id, week_start }) => [
        { type: 'WPReview', id: `${member_user_id}-${week_start}` },
        'WPPlans',
      ],
    }),
  }),
})

export const {
  useGetWPPlansQuery,
  useGetWPPlanDetailQuery,
  useCreateWPPlanMutation,
  useUpdateWPPlanMutation,
  useGetWPProjectsQuery,
  useGetWPBacklogProjectsQuery,
  useGetWPTasksQuery,
  useGetWPTaskStatusesQuery,
  useLazyGetWPPreviousWeekReviewQuery,
  useGetWPPreviousWeekReviewQuery,
  useAcknowledgeWPPreviousWeekMutation,
} = weeklyPlanningApi
